unit Bcl.Lang;

{$I Bcl.inc}

interface

uses
  SysUtils, Classes, Types, StrUtils,
{$IFDEF PAS2JS}
  JS, Web,
{$ELSE}
  Character,
  Bcl.Json,
  Bcl.Json.Classes,
  IOUtils,
{$ENDIF}
  Generics.Collections;

type
  /// <summary>Represents a parsed locale identifier with language, region, and script components.</summary>
  ILocale = interface
  ['{8F3A1C2E-5B4D-4F1A-9E6C-7D8A3B2C1E0F}']
    function GetLanguage: string;
    function GetRegion: string;
    function GetScript: string;
    function GetName: string;
    function GetOriginalName: string;

    /// <summary>Gets the normalized locale name composed from language, script, and region.</summary>
    property Name: string read GetName;
    /// <summary>Gets the original locale name string as provided during creation.</summary>
    property OriginalName: string read GetOriginalName;
    /// <summary>Gets the ISO 639 language code (e.g., "en", "fr", "zh").</summary>
    property Language: string read GetLanguage;
    /// <summary>Gets the ISO 3166 region code (e.g., "US", "FR", "CN").</summary>
    property Region: string read GetRegion;
    /// <summary>Gets the ISO 15924 script code (e.g., "Hans", "Hant").</summary>
    property Script: string read GetScript;
  end;

  /// <summary>Interface for retrieving localized text translations by message identifier.</summary>
  ITextLocalizer = interface
  ['{74288F6D-26AE-45C1-AEF4-5C7DABBC1F1F}']
    /// <summary>Returns the locale name associated with this localizer.</summary>
    /// <returns>The locale name string.</returns>
    function LocaleName: string;
    /// <summary>Returns the localized text for the specified message identifier.</summary>
    /// <param name="MsgId">The message identifier to look up.</param>
    /// <returns>The localized text, or <c>MsgId</c> itself if no translation is found.</returns>
    function GetText(const MsgId: string): string;
  end;

  TDictionaryLocalizer = class(TInterfacedObject, ITextLocalizer)
  strict private
    FLocale: ILocale;
    FResources: TDictionary<string, string>;
  public
    constructor Create(const ALocale: ILocale);
    destructor Destroy; override;
    function LocaleName: string;
    function GetText(const MsgId: string): string;
    procedure AddText(const MsgId, MsgStr: string);
    property Locale: ILocale read FLocale;
  end;

  /// <summary>Factory interface for creating or retrieving text localizers by locale name.</summary>
  ITextLocalizerFactory = interface
    /// <summary>Returns a text localizer for the specified locale name.</summary>
    /// <param name="LocaleName">The locale name to retrieve a localizer for.</param>
    /// <returns>The text localizer for the locale, or <c>nil</c> if not found.</returns>
    function GetLocalizer(const LocaleName: string): ITextLocalizer; {$IFDEF PAS2JS}async;{$ENDIF}
  end;

  /// <summary>Manages global and per-thread text localizers for retrieving translated strings.</summary>
  /// <remarks>
  ///   Provides access to the current localizer through <c>Current</c>, which returns the
  ///   thread-specific localizer if set, otherwise falls back to the global localizer.
  /// </remarks>
  TLocalizer = class
  strict private class var
//    FDefaultLanguage: string;
    FGlobal: ITextLocalizer;
  strict private
    class function FindLocalizer(Factory: ITextLocalizerFactory;
      const LocaleName: string): ITextLocalizer; overload; {$IFDEF PAS2JS}async;{$ENDIF}
  public
    class constructor Create;
    class destructor Destroy;
    /// <summary>Returns the global text localizer.</summary>
    /// <returns>The global <c>ITextLocalizer</c> instance, or <c>nil</c> if not set.</returns>
    class function Global: ITextLocalizer;
    /// <summary>Returns the text localizer for the current thread.</summary>
    /// <returns>The thread-specific <c>ITextLocalizer</c> instance, or <c>nil</c> if not set.</returns>
    class function CurrentThread: ITextLocalizer;
    /// <summary>Returns the active text localizer, preferring the thread-specific localizer over the global one.</summary>
    /// <returns>The current thread localizer if set; otherwise, the global localizer.</returns>
    class function Current: ITextLocalizer;

    /// <summary>Finds and returns a text localizer for the specified locale name.</summary>
    /// <param name="LocaleName">The locale name to search for.</param>
    /// <returns>The matching <c>ITextLocalizer</c>, or <c>nil</c> if not found.</returns>
    class function FindLocalizer(const LocaleName: string): ITextLocalizer; overload; {$IFDEF PAS2JS}async;{$ENDIF}
    /// <summary>Sets the global language to the specified locale.</summary>
    /// <param name="LocaleName">The locale name to set as the global language.</param>
    class procedure SetGlobalLanguage(const LocaleName: string); {$IFDEF PAS2JS}async;{$ENDIF}
    /// <summary>Sets the current thread language to the specified locale.</summary>
    /// <param name="LocaleName">The locale name to set for the current thread.</param>
    class procedure SetCurrentThreadLanguage(const LocaleName: string); {$IFDEF PAS2JS}async;{$ENDIF}
    /// <summary>Clears the current thread language, reverting to the global localizer.</summary>
    class procedure FinalizeCurrentThreadLanguage;
  end;

  /// <summary>Provides initialization and configuration of the localization system.</summary>
  TLang = class
  class var
    FLocalizerFactory: ITextLocalizerFactory;
  protected
    class function LocalizerFactory: ITextLocalizerFactory;
  public
    /// <summary>Initializes the localization system with the default localizer factory.</summary>
    class procedure Init;

    /// <summary>Sets the localizer factory used to create text localizers.</summary>
    /// <param name="ALocalizerFactory">The factory to use for creating localizers.</param>
    class procedure SetLocalizerFactory(ALocalizerFactory: ITextLocalizerFactory);
  end;

{$IFDEF PAS2JS}
  TProc = reference to procedure;

  TJsonTextLocalizer = class(TInterfacedObject, ITextLocalizer)
  strict private
    FLocaleName: string;
    FObj: TJSObject;
  public
    constructor Create(const ALocaleName: string; AObj: TJSObject);
    function LocaleName: string;
    function GetText(const MsgId: string): string;
  end;

  TXhrLocalizerFactory = class(TInterfacedObject, ITextLocalizerFactory)
  strict private
    FObj: TJSObject;
    function LoadJsonFromUrl(const Url: string): TJSObject; {$IFDEF PAS2JS}async;{$ENDIF}
    function LoadJsonResources(const LocaleName: string): TJSObject; {$IFDEF PAS2JS}async;{$ENDIF}
    function GetLocalizer(const LocaleName: string): ITextLocalizer; {$IFDEF PAS2JS}async;{$ENDIF}
  public
    constructor Create;
  end;
{$ENDIF}

{$IFNDEF PAS2JS}
  TDefaultLocalizerFactory = class(TInterfacedObject, ITextLocalizerFactory)
  strict private
    FLocalizers: TDictionary<string, ITextLocalizer>;
    function GetLocalizer(const LocaleName: string): ITextLocalizer;
    procedure AddLanguageFromJson(const LocaleName: string; Data: TJObject);
    procedure LoadFromFolder(const Folder: string);
    procedure LoadFromResource(const ResourceName: string);
  public
    constructor Create(const LangResource: string = ''; LangFolder: string = '');
    destructor Destroy; override;
  end;
{$ENDIF}

{$IFNDEF PAS2JS}
  /// <summary>Implements <c>ILocale</c> by parsing a locale name string into its language, script, and region components.</summary>
  /// <remarks>
  ///   Parses locale strings in BCP 47 format (e.g., "en-US", "zh-Hans-CN").
  ///   Supports both hyphen and underscore separators.
  /// </remarks>
  TLocale = class(TInterfacedObject, ILocale)
  strict private
    FOriginalName: string;
    FLanguage: string;
    FScript: string;
    FRegion: string;
    function GetLanguage: string;
    function GetRegion: string;
    function GetScript: string;
    function GetName: string;
    function GetOriginalName: string;
  strict private
    procedure ParseLocale;
  public
    /// <summary>Creates a locale by parsing the specified locale name string.</summary>
    /// <param name="ALocaleName">The locale name to parse (e.g., "en-US", "zh-Hans-CN").</param>
    constructor Create(const ALocaleName: string);
    /// <summary>Gets the original locale name string as provided during creation.</summary>
    property OriginalName: string read GetOriginalName;
    /// <summary>Gets the normalized locale name composed from language, script, and region.</summary>
    property Name: string read GetName;
    /// <summary>Gets the ISO 639 language code.</summary>
    property Language: string read GetLanguage;
    /// <summary>Gets the ISO 3166 region code.</summary>
    property Region: string read GetRegion;
    /// <summary>Gets the ISO 15924 script code.</summary>
    property Script: string read GetScript;
  end;
{$ENDIF}

{$IFDEF PAS2JS}
procedure Translate(Element: TJSElement);
{$ELSE}
procedure Translate(Element: TObject);
{$ENDIF}
/// <summary>Returns the localized text for the specified message identifier using the current localizer.</summary>
/// <param name="MsgId">The message identifier to look up.</param>
/// <returns>The localized text, or <c>MsgId</c> itself if no localizer is active or no translation is found.</returns>
function GetText(const MsgId: string): string;
/// <summary>Shorthand alias for <see cref="Bcl.Lang|GetText"/>.</summary>
/// <param name="MsgId">The message identifier to look up.</param>
/// <returns>The localized text, or <c>MsgId</c> itself if no translation is found.</returns>
function _(const MsgId: string): string;

{$IFDEF DEBUG}
{$IFDEF PAS2JS}
var
  Texts: TJSObject;
{$ENDIF}
{$ENDIF}

type
  TLanguageTag = record
    Code: string;
    Description: string;
  end;

const
  LanguageTags: array[0..131] of TLanguageTag = (
    (Code: 'af-ZA'; Description: 'Afrikaans - South Africa'),
    (Code: 'sq-AL'; Description: 'Albanian - Albania'),
    (Code: 'ar-DZ'; Description: 'Arabic - Algeria'),
    (Code: 'ar-BH'; Description: 'Arabic - Bahrain'),
    (Code: 'ar-EG'; Description: 'Arabic - Egypt'),
    (Code: 'ar-IQ'; Description: 'Arabic - Iraq'),
    (Code: 'ar-JO'; Description: 'Arabic - Jordan'),
    (Code: 'ar-KW'; Description: 'Arabic - Kuwait'),
    (Code: 'ar-LB'; Description: 'Arabic - Lebanon'),
    (Code: 'ar-LY'; Description: 'Arabic - Libya'),
    (Code: 'ar-MA'; Description: 'Arabic - Morocco'),
    (Code: 'ar-OM'; Description: 'Arabic - Oman'),
    (Code: 'ar-QA'; Description: 'Arabic - Qatar'),
    (Code: 'ar-SA'; Description: 'Arabic - Saudi Arabia'),
    (Code: 'ar-SY'; Description: 'Arabic - Syria'),
    (Code: 'ar-TN'; Description: 'Arabic - Tunisia'),
    (Code: 'ar-AE'; Description: 'Arabic - United Arab Emirates'),
    (Code: 'ar-YE'; Description: 'Arabic - Yemen'),
    (Code: 'hy-AM'; Description: 'Armenian - Armenia'),
    (Code: 'eu-ES'; Description: 'Basque - Basque'),
    (Code: 'be-BY'; Description: 'Belarusian - Belarus'),
    (Code: 'bg-BG'; Description: 'Bulgarian - Bulgaria'),
    (Code: 'ca-ES'; Description: 'Catalan - Catalan'),
    (Code: 'zh-CN'; Description: 'Chinese - China'),
    (Code: 'zh-HK'; Description: 'Chinese - Hong Kong SAR'),
    (Code: 'zh-MO'; Description: 'Chinese - Macau SAR'),
    (Code: 'zh-SG'; Description: 'Chinese - Singapore'),
    (Code: 'zh-TW'; Description: 'Chinese - Taiwan'),
    (Code: 'hr-HR'; Description: 'Croatian - Croatia'),
    (Code: 'cs-CZ'; Description: 'Czech - Czech Republic'),
    (Code: 'da-DK'; Description: 'Danish - Denmark'),
    (Code: 'div-M'; Description: 'Dhivehi - Maldives'),
    (Code: 'nl-BE'; Description: 'Dutch - Belgium'),
    (Code: 'nl-NL'; Description: 'Dutch - The Netherlands'),
    (Code: 'en-AU'; Description: 'English - Australia'),
    (Code: 'en-BZ'; Description: 'English - Belize'),
    (Code: 'en-CA'; Description: 'English - Canada'),
    (Code: 'en-CB'; Description: 'English - Caribbean'),
    (Code: 'en-IE'; Description: 'English - Ireland'),
    (Code: 'en-JM'; Description: 'English - Jamaica'),
    (Code: 'en-NZ'; Description: 'English - New Zealand'),
    (Code: 'en-PH'; Description: 'English - Philippines'),
    (Code: 'en-ZA'; Description: 'English - South Africa'),
    (Code: 'en-TT'; Description: 'English - Trinidad and Tobago'),
    (Code: 'en-GB'; Description: 'English - United Kingdom'),
    (Code: 'en-US'; Description: 'English - United States'),
    (Code: 'en-ZW'; Description: 'English - Zimbabwe'),
    (Code: 'et-EE'; Description: 'Estonian - Estonia'),
    (Code: 'fo-FO'; Description: 'Faroese - Faroe Islands'),
    (Code: 'fa-IR'; Description: 'Farsi - Iran'),
    (Code: 'fi-FI'; Description: 'Finnish - Finland'),
    (Code: 'fr-BE'; Description: 'French - Belgium'),
    (Code: 'fr-CA'; Description: 'French - Canada'),
    (Code: 'fr-FR'; Description: 'French - France'),
    (Code: 'fr-LU'; Description: 'French - Luxembourg'),
    (Code: 'fr-MC'; Description: 'French - Monaco'),
    (Code: 'fr-CH'; Description: 'French - Switzerland'),
    (Code: 'gl-ES'; Description: 'Galician - Galician'),
    (Code: 'ka-GE'; Description: 'Georgian - Georgia'),
    (Code: 'de-AT'; Description: 'German - Austria'),
    (Code: 'de-DE'; Description: 'German - Germany'),
    (Code: 'de-LI'; Description: 'German - Liechtenstein'),
    (Code: 'de-LU'; Description: 'German - Luxembourg'),
    (Code: 'de-CH'; Description: 'German - Switzerland'),
    (Code: 'el-GR'; Description: 'Greek - Greece'),
    (Code: 'gu-IN'; Description: 'Gujarati - India'),
    (Code: 'he-IL'; Description: 'Hebrew - Israel'),
    (Code: 'hi-IN'; Description: 'Hindi - India'),
    (Code: 'hu-HU'; Description: 'Hungarian - Hungary'),
    (Code: 'is-IS'; Description: 'Icelandic - Iceland'),
    (Code: 'id-ID'; Description: 'Indonesian - Indonesia'),
    (Code: 'it-IT'; Description: 'Italian - Italy'),
    (Code: 'it-CH'; Description: 'Italian - Switzerland'),
    (Code: 'ja-JP'; Description: 'Japanese - Japan'),
    (Code: 'kn-IN'; Description: 'Kannada - India'),
    (Code: 'kk-KZ'; Description: 'Kazakh - Kazakhstan'),
    (Code: 'kok-I'; Description: 'Konkani - India'),
    (Code: 'ko-KR'; Description: 'Korean - Korea'),
    (Code: 'ky-KZ'; Description: 'Kyrgyz - Kazakhstan'),
    (Code: 'lv-LV'; Description: 'Latvian - Latvia'),
    (Code: 'lt-LT'; Description: 'Lithuanian - Lithuania'),
    (Code: 'mk-MK'; Description: 'Macedonian (FYROM)'),
    (Code: 'ms-BN'; Description: 'Malay - Brunei'),
    (Code: 'ms-MY'; Description: 'Malay - Malaysia'),
    (Code: 'mr-IN'; Description: 'Marathi - India'),
    (Code: 'mn-MN'; Description: 'Mongolian - Mongolia'),
    (Code: 'nb-NO'; Description: 'Norwegian (Bokmal) - Norway'),
    (Code: 'nn-NO'; Description: 'Norwegian (Nynorsk) - Norway'),
    (Code: 'pl-PL'; Description: 'Polish - Poland'),
    (Code: 'pt-BR'; Description: 'Portuguese - Brazil'),
    (Code: 'pt-PT'; Description: 'Portuguese - Portugal'),
    (Code: 'pa-IN'; Description: 'Punjabi - India'),
    (Code: 'ro-RO'; Description: 'Romanian - Romania'),
    (Code: 'ru-RU'; Description: 'Russian - Russia'),
    (Code: 'sa-IN'; Description: 'Sanskrit - India'),
    (Code: 'Cy-sr-SP'; Description: 'Serbian (Cyrillic) - Serbia'),
    (Code: 'Lt-sr-SP'; Description: 'Serbian (Latin) - Serbia'),
    (Code: 'sk-SK'; Description: 'Slovak - Slovakia'),
    (Code: 'sl-SI'; Description: 'Slovenian - Slovenia'),
    (Code: 'es-AR'; Description: 'Spanish - Argentina'),
    (Code: 'es-BO'; Description: 'Spanish - Bolivia'),
    (Code: 'es-CL'; Description: 'Spanish - Chile'),
    (Code: 'es-CO'; Description: 'Spanish - Colombia'),
    (Code: 'es-CR'; Description: 'Spanish - Costa Rica'),
    (Code: 'es-DO'; Description: 'Spanish - Dominican Republic'),
    (Code: 'es-EC'; Description: 'Spanish - Ecuador'),
    (Code: 'es-SV'; Description: 'Spanish - El Salvador'),
    (Code: 'es-GT'; Description: 'Spanish - Guatemala'),
    (Code: 'es-HN'; Description: 'Spanish - Honduras'),
    (Code: 'es-MX'; Description: 'Spanish - Mexico'),
    (Code: 'es-NI'; Description: 'Spanish - Nicaragua'),
    (Code: 'es-PA'; Description: 'Spanish - Panama'),
    (Code: 'es-PY'; Description: 'Spanish - Paraguay'),
    (Code: 'es-PE'; Description: 'Spanish - Peru'),
    (Code: 'es-PR'; Description: 'Spanish - Puerto Rico'),
    (Code: 'es-ES'; Description: 'Spanish - Spain'),
    (Code: 'es-UY'; Description: 'Spanish - Uruguay'),
    (Code: 'es-VE'; Description: 'Spanish - Venezuela'),
    (Code: 'sw-KE'; Description: 'Swahili - Kenya'),
    (Code: 'sv-FI'; Description: 'Swedish - Finland'),
    (Code: 'sv-SE'; Description: 'Swedish - Sweden'),
    (Code: 'syr-S'; Description: 'Syriac - Syria'),
    (Code: 'ta-IN'; Description: 'Tamil - India'),
    (Code: 'tt-RU'; Description: 'Tatar - Russia'),
    (Code: 'te-IN'; Description: 'Telugu - India'),
    (Code: 'th-TH'; Description: 'Thai - Thailand'),
    (Code: 'tr-TR'; Description: 'Turkish - Turkey'),
    (Code: 'uk-UA'; Description: 'Ukrainian - Ukraine'),
    (Code: 'ur-PK'; Description: 'Urdu - Pakistan'),
    (Code: 'Cy-uz-UZ'; Description: 'Uzbek (Cyrillic) - Uzbekistan'),
    (Code: 'Lt-uz-UZ'; Description: 'Uzbek (Latin) - Uzbekistan'),
    (Code: 'vi-VN'; Description: 'Vietnamese - Vietnam')
  );

{$IFNDEF PAS2JS}
/// <summary>Finds the best matching locale from a list of candidates for the requested locale.</summary>
/// <param name="Requested">The requested locale to match against.</param>
/// <param name="Candidates">The array of available candidate locales.</param>
/// <returns>The best matching locale, or <c>nil</c> if no candidate matches the requested language.</returns>
/// <remarks>Uses a scoring algorithm that considers language, region, and script. Includes special handling for Chinese script variants.</remarks>
function MatchLocale(const Requested: ILocale; const Candidates: TArray<ILocale>): ILocale;
{$ENDIF}

implementation

uses
  Bcl.Utils;

{$IFDEF PAS2JS}
var
{$ELSE}
threadvar
{$ENDIF}
  FCurrentThread: ITextLocalizer;

function _(const MsgId: string): string;
begin
  Result := GetText(MsgId);
end;

function GetText(const MsgId: string): string;
var
  Localizer: ITextLocalizer;
begin
  Localizer := TLocalizer.Current;
  if Localizer <> nil then
    Result := Localizer.GetText(MsgId)
  else
    Result := MsgId;

{$IFDEF DEBUG}
{$IFDEF PAS2JS}
  if Texts <> nil then
    Texts[MsgId] := '';
{$ENDIF}
{$ENDIF}
end;

{$IFDEF PAS2JS}
procedure Translate(Element: TJSElement);
const
  cTranslatableClass = 'translatable';
var
  Elements: TJSHtmlCollection;
  Node: TJSNode;
  I: Integer;
begin
  Elements := document.getElementsByClassName(cTranslatableClass);
  for I := 0 to Elements.length - 1 do
  begin
    Node := Elements.item(I);
    Node.textContent := GetText(TJSString(Node.textContent).trim);
  end;
end;
{$ELSE}
procedure Translate(Element: TObject);
begin
end;
{$ENDIF}

{$IFNDEF PAS2JS}
function MatchLocale(const Requested: ILocale; const Candidates: TArray<ILocale>): ILocale;
var
  PreferredZhScript: string;
  Candidate: ILocale;
  BestScore: Integer;
  Score: Integer;
begin
  // special treatement for Chinese
  PreferredZhScript := '';
  if (Requested.Language = 'zh') and (Requested.Region <> '') and (Requested.Script = '') then
  begin
    if (Requested.Region = 'CN') or (Requested.Region = 'SG') or (Requested.Region = 'MY') then
      PreferredZhScript := 'Hans';
    if (Requested.Region = 'TW') or (Requested.Region = 'HK') or (Requested.Region = 'MO') then
      PreferredZhScript := 'Hant';
  end;

  Result := nil;
  BestScore := 0;
  for Candidate in Candidates do
  begin
    // language must match
    if Candidate.Language <> Requested.Language then Continue;

    // language matches
    Score := 100;

    // Special treatement if request had script
    if Requested.Script <> '' then
    begin
      if Requested.Script = Candidate.Script then
        Inc(Score, 40);

      if Requested.Script <> Candidate.Script then
        Continue;
    end
    else
    if (PreferredZhScript <> '') and (Candidate.Script = PreferredZhScript) then
      Inc(Score, 15);

    // Region matches
    if Requested.Region <> '' then
    begin
      if Requested.Region = Candidate.Region  then
        Inc(Score, 20)
      else
      if Candidate.Region = '' then
        Inc(Score, 5)
      else
      if SameText(Candidate.Region, Requested.Language) then // special case to prefer fr-fr to fr-ca if fr is requested
        Inc(Score, 5);
    end
    else
    begin
      if SameText(Candidate.Region, Requested.Language) then // special case to prefer fr-fr to fr-ca if fr is requested
        Inc(Score, 5);
      if Candidate.Region <> '' then
        Inc(Score, 2);
      if Candidate.Script <> '' then
        Inc(Score, 1);
    end;

    if Score > BestScore then
    begin
      Result := Candidate;
      BestScore := Score;
    end;
  end;
end;
{$ENDIF}

{ TLocale }

{$IFNDEF PAS2JS}
constructor TLocale.Create(const ALocaleName: string);
begin
  inherited Create;
  FOriginalName := ALocaleName;
  ParseLocale;
end;
{$ENDIF}

{$IFNDEF PAS2JS}
function TLocale.GetLanguage: string;
begin
  Result := FLanguage;
end;
{$ENDIF}

{$IFNDEF PAS2JS}
function TLocale.GetOriginalName: string;
begin
  Result := FOriginalName;
end;
{$ENDIF}

{$IFNDEF PAS2JS}
function TLocale.GetName: string;
begin
  Result := Language;
  if Script <> '' then
    Result := Result + '-' + Script;
  if Region <> '' then
    Result := Result + '-' + Region;
end;
{$ENDIF}

{$IFNDEF PAS2JS}
function TLocale.GetRegion: string;
begin
  Result := FRegion;
end;
{$ENDIF}

{$IFNDEF PAS2JS}
function TLocale.GetScript: string;
begin
  Result := FScript;
end;
{$ENDIF}

{$IFNDEF PAS2JS}
procedure TLocale.ParseLocale;
var
  Parts: TStringDynArray;
  I: Integer;
  P: string;
begin
  Parts := SplitString(StringReplace(Trim(OriginalName), '_', '-', [rfReplaceAll]), '-');
  if Length(Parts) = 0 then Exit;

  FLanguage := LowerCase(Parts[0]);
  for I := 1 to Length(Parts) - 1 do
  begin
    P := Parts[I];
    if (Length(P) = 4) and (FScript = '') and TBclUtils.IsLetter(P[1]) and TBclUtils.IsLetter(P[2])
      and TBclUtils.IsLetter(P[3]) and TBclUtils.IsLetter(P[4]) then
    begin
      FScript := UpperCase(P[1]) + LowerCase(Copy(P, 2));
      Continue;
    end;

    if FRegion = '' then
    begin
      if (Length(P) = 2) and TBclUtils.IsLetter(P[1]) and TBclUtils.IsLetter(P[2]) then
      begin
        FRegion := UpperCase(P);
        Continue;
      end;

      if (Length(P) = 3) and TBclUtils.IsDigit(P[1]) and TBclUtils.IsDigit(P[2]) and TBclUtils.IsDigit(P[3]) then
      begin
        FRegion := P;
        Continue;
      end;
    end;
  end;
end;
{$ENDIF}

{ TTextLocalizer }

procedure TDictionaryLocalizer.AddText(const MsgId, MsgStr: string);
begin
  FResources.AddOrSetValue(Msgid, MsgStr);
end;

constructor TDictionaryLocalizer.Create(const ALocale: ILocale);
begin
  inherited Create;
  FLocale := ALocale;
  FResources := TDictionary<string, string>.Create;
end;

destructor TDictionaryLocalizer.Destroy;
begin
  FResources.Free;
  inherited;
end;

function TDictionaryLocalizer.GetText(const MsgId: string): string;
begin
  if not FResources.TryGetValue(MsgId, Result) then
    Result := MsgId;
end;

function TDictionaryLocalizer.LocaleName: string;
begin
  Result := FLocale.Name;
end;

{ TLocalizer }

class constructor TLocalizer.Create;
begin
{$IFDEF PAS2JS}
//  if JS.isString(window.navigator.language) then
//    FDefaultLanguage := string(window.navigator.language)
//  else
//    FDefaultLanguage := 'en-us';
{$ELSE}

{$ENDIF}
end;

class function TLocalizer.Current: ITextLocalizer;
begin
  if FCurrentThread <> nil then
    Result := FCurrentThread
  else
    Result := FGlobal;
end;

class function TLocalizer.CurrentThread: ITextLocalizer;
begin
  Result := FCurrentThread;
end;

class destructor TLocalizer.Destroy;
begin
end;

class procedure TLocalizer.FinalizeCurrentThreadLanguage;
begin
  FCurrentThread := nil;
end;

class function TLocalizer.FindLocalizer(const LocaleName: string): ITextLocalizer;
begin
{$IFDEF PAS2JS}
  Result := await(FindLocalizer(TLang.LocalizerFactory, LocaleName));
{$ELSE}
  Result := FindLocalizer(TLang.LocalizerFactory, LocaleName);
{$ENDIF}
end;

class function TLocalizer.FindLocalizer(Factory: ITextLocalizerFactory;
  const LocaleName: string): ITextLocalizer;
begin
  if Factory = nil then
    Exit(nil);

{$IFDEF PAS2JS}
  Result := await(Factory.GetLocalizer(LocaleName));
  if Result = nil then
    Result := await(Factory.GetLocalizer(Copy(LocaleName, 1, Pos('-', LocaleName) - 1)));
{$ELSE}
  Result := Factory.GetLocalizer(LocaleName);
  if Result = nil then
    Result := Factory.GetLocalizer(Copy(LocaleName, 1, Pos('-', LocaleName) - 1));
{$ENDIF}
end;

class function TLocalizer.Global: ITextLocalizer;
begin
  Result := FGlobal;
end;

class procedure TLocalizer.SetCurrentThreadLanguage(const LocaleName: string);
begin
{$IFDEF PAS2JS}
  FCurrentThread := await(FindLocalizer(TLang.LocalizerFactory, LocaleName));
{$ELSE}
  FCurrentThread := FindLocalizer(TLang.LocalizerFactory, LocaleName);
{$ENDIF}
end;

class procedure TLocalizer.SetGlobalLanguage(const LocaleName: string);
var
  TempGlobal: ITextLocalizer;
begin
{$IFDEF PAS2JS}
  TempGlobal := await(FindLocalizer(TLang.LocalizerFactory, LocaleName));
{$ELSE}
  TempGlobal := FindLocalizer(TLang.LocalizerFactory, LocaleName);
{$ENDIF}
  FGlobal := TempGlobal;
end;

{ TLang }

class procedure TLang.Init;
begin
{$IFDEF PAS2JS}
  SetLocalizerFactory(TXhrLocalizerFactory.Create);
{$ELSE}
  SetLocalizerFactory(TDefaultLocalizerFactory.Create);
{$ENDIF}
end;

class function TLang.LocalizerFactory: ITextLocalizerFactory;
begin
  Result := FLocalizerFactory;
end;

class procedure TLang.SetLocalizerFactory(ALocalizerFactory: ITextLocalizerFactory);
begin
  FLocalizerFactory := ALocalizerFactory;
end;

{ TXhrLocalizerFactory }

{$IFDEF PAS2JS}
constructor TXhrLocalizerFactory.Create;
begin
  inherited Create;
end;

function TXhrLocalizerFactory.GetLocalizer(const LocaleName: string): ITextLocalizer;
begin
  if FObj = nil then
  begin
//    FObj := await(LoadJsonResources('global'));
    if FObj = nil then
      FObj := TJSObject.new;
  end;

  if JS.IsUndefined(FObj[LocaleName]) then
    FObj[LocaleName] := await(LoadJsonResources(LocaleName));

  if JS.IsObject(FObj[LocaleName]) then
    Result := TJsonTextLocalizer.Create(LocaleName, JS.ToObject(FObj[LocaleName]))
  else
    Result := nil;
end;

procedure InternalSendRequest(Xhr: TJSXmlHttpRequest;
  SuccessProc: TProc; ErrorProc: TProc);

  procedure XhrLoad;
  begin
    if Assigned(SuccessProc) then
      SuccessProc();
  end;

  procedure XhrError;
  begin
    if Assigned(ErrorProc) then
      ErrorProc();
  end;

begin
  Xhr.addEventListener('load', @XhrLoad);
  Xhr.addEventListener('error', @XhrError);
  Xhr.addEventListener('timeout', @XhrError);
  Xhr.send;
end;

function TXhrLocalizerFactory.LoadJsonResources(const LocaleName: string): TJSObject;
const
  LangFileUrl = 'languages/%s.json';
var
  RequestUrl: string;
begin
  RequestUrl := Format(LangFileUrl, [LocaleName]);
  Result := await(LoadJsonFromUrl(RequestUrl));
end;

function TXhrLocalizerFactory.LoadJsonFromUrl(const Url: string): TJSObject;
var
  Promise: TJSPromise;
begin
  Promise := TJSPromise.new(
    procedure(resolve, reject: TJSPromiseResolver)
    var
      Xhr: TJSXMLHttpRequest;
    begin
      Xhr := TJSXMLHttpRequest.new;
      Xhr.open('GET', Url, True);
      InternalSendRequest(
        Xhr,
        procedure
        var
          Obj: TJSObject;
        begin
          Obj := nil;
          if Xhr.status = 200 then
          try
            Obj := TJSJson.parseObject(Xhr.responseText);
          except
          end;
          resolve(Obj);
        end,
        procedure
        begin
          resolve(nil);
        end
      );
    end
  );
  Exit(Promise);
end;
{$ENDIF}

{ TJsonTextLocalizer }

{$IFDEF PAS2JS}
constructor TJsonTextLocalizer.Create(const ALocaleName: string; AObj: TJSObject);
begin
  inherited Create;
  FLocaleName := ALocaleName;
  FObj := AObj;
end;

function TJsonTextLocalizer.GetText(const MsgId: string): string;
var
  Value: JSValue;
begin
  Value := FObj[MsgId];
  if JS.IsString(Value) then
    Result := string(Value)
  else
    Result := MsgId;
end;

function TJsonTextLocalizer.LocaleName: string;
begin
  Result := FLocaleName;
end;
{$ENDIF}

{ TDefaultLocalizerFactory }

{$IFNDEF PAS2JS}
procedure TDefaultLocalizerFactory.AddLanguageFromJson(const LocaleName: string; Data: TJObject);
var
  Localizer: ITextLocalizer;
  Member: TJMember;
  Locale: ILocale;
begin
  Locale := TLocale.Create(LocaleName);
  if not FLocalizers.TryGetValue(Locale.Name, Localizer) then
  begin
    Localizer := TDictionaryLocalizer.Create(Locale);
    FLocalizers.Add(Locale.Name, Localizer);
  end;
  for Member in Data do
    if Member.Value.IsString then
      (Localizer as TDictionaryLocalizer).AddText(Member.Name, Member.Value.AsString);
end;

constructor TDefaultLocalizerFactory.Create(const LangResource: string = ''; LangFolder: string = '');
begin
  inherited Create;
  FLocalizers := TDictionary<string, ITextLocalizer>.Create;
  if LangResource <> '' then
    LoadFromResource(LangResource)
  else
    LoadFromResource('BIZ_LANGUAGES');

  if LangFolder <> '' then
    LoadFromFolder(LangFolder)
  else
    LoadFromFolder(TPath.Combine(TPath.GetDirectoryName(ParamStr(0)), 'languages'))
end;

destructor TDefaultLocalizerFactory.Destroy;
begin
  FLocalizers.Free;
  inherited;
end;

function TDefaultLocalizerFactory.GetLocalizer(const LocaleName: string): ITextLocalizer;
var
  Requested: ILocale;
  Found: ILocale;
  I: Integer;
  Candidates: TArray<ILocale>;
  Localizers: TArray<ITextLocalizer>;
begin
  Requested := TLocale.Create(LocaleName);
  if Requested.Name = '' then Exit(nil);

  Localizers := FLocalizers.Values.ToArray;
  SetLength(Candidates, Length(Localizers));
  for I := 0 to Length(Localizers) - 1 do
    Candidates[I] := (Localizers[I] as TDictionaryLocalizer).Locale;

  Found := MatchLocale(Requested, Candidates);
  Result := nil;
  if Found <> nil then
    FLocalizers.TryGetValue(Found.Name, Result);
end;

procedure TDefaultLocalizerFactory.LoadFromFolder(const Folder: string);
var
  FileName: string;
  Json: string;
  Data: TJObject;
begin
  if not TDirectory.Exists(Folder) then
    Exit;

  for FileName in TDirectory.GetFiles(Folder, '*.json', TSearchOption.soTopDirectoryOnly) do
  begin
    Json := TFile.ReadAllText(FileName, TEncoding.UTF8);
    try
      Data := TJson.Deserialize<TJObject>(Json);
    except
      on E: Exception do
        raise Exception.CreateFmt('Error loading language file %s: %s (%s)',
          [TPath.GetFileName(FileName), E.Message, E.ClassName]);
    end;
    try
      AddLanguageFromJson(TPath.GetFileNameWithoutExtension(FileName), Data);
    finally
      Data.Free;
    end;
  end;
end;
procedure TDefaultLocalizerFactory.LoadFromResource(const ResourceName: string);
var
  Stream: TStream;
  Languages: TJObject;
  Member: TJMember;
begin
  if FindResource(HInstance, PChar(ResourceName), RT_RCDATA) = 0 then Exit;

  Stream := TResourceStream.Create(HInstance, PChar(ResourceName), RT_RCDATA);
  try
    try
      Languages := TJson.Deserialize<TJObject>(Stream);
      try
        for Member in Languages do
          if Member.Value.IsObject then
            AddLanguageFromJson(Member.Name, Member.Value.AsObject);
      finally
        Languages.Free;
      end;
    except
      on E: Exception do
        raise Exception.CreateFmt('Error loading language resource %s (%s)',
          [ResourceName, E.Message, E.ClassName]);
    end;
  finally
    Stream.Free;
  end;
end;
{$ENDIF}

end.
